import { Check } from 'lucide-react';
import type { StepDef } from '@/lib/practice/wizardSteps';
import { canJumpTo } from '@/lib/practice/wizardSteps';
import { MatchCountChip } from './MatchCountChip';

/**
 * The wizard's progress rail — ROUTE_INVENTORY §2.11 #36.
 *
 * Jump-BACK only: forward movement goes through Next, so a user cannot land on
 * Review without passing the steps it summarises. A future step is a real
 * `disabled` button rather than a styled-down one, so the block is announced
 * rather than just looked.
 *
 * The step list it renders has already had the Lessons step dropped when there
 * is nothing to pick (§2.11 #35), which is why nothing here indexes by
 * position.
 */
export function Stepper({
  steps,
  currentIndex,
  onJump,
  matchCount,
  isLoading,
}: {
  steps: StepDef[];
  currentIndex: number;
  onJump: (index: number) => void;
  matchCount: number | null;
  isLoading: boolean;
}) {
  return (
    <div className='flex-shrink-0 border-b border-border bg-muted/40 px-6 py-3 dark:bg-white/[0.02]'>
      <div className='flex items-center justify-between gap-3'>
        <ol className='flex flex-wrap items-center gap-x-2 gap-y-2 text-xs'>
          {steps.map((s, i) => {
            const isActive = i === currentIndex;
            const isDone = i < currentIndex;
            const reachable = canJumpTo(i, currentIndex);
            // Done = neutral filled (gray) with a check, so the stepper
            // doesn't paint the bar indigo. Active is the only indigo
            // accent - your single "you are here" signal.
            const dotCls = isActive
              ? 'bg-primary text-primary-foreground'
              : isDone
                ? 'bg-gray-300 text-foreground dark:bg-white/[0.15]'
                : 'bg-muted text-muted-foreground ring-1 ring-inset ring-ring';
            const labelCls = isActive
              ? 'text-foreground font-semibold'
              : isDone
                ? 'text-muted-foreground'
                : 'text-muted-foreground';
            return (
              <li key={s.key} className='flex items-center gap-2'>
                <button
                  type='button'
                  onClick={() => reachable && onJump(i)}
                  disabled={!reachable}
                  className={`group flex items-center gap-1.5 ${
                    reachable ? 'cursor-pointer' : 'cursor-not-allowed'
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums motion-safe:transition-colors ${dotCls}`}
                  >
                    {isDone ? <Check className='h-3 w-3' /> : i + 1}
                  </span>
                  <span className={`${labelCls}`}>{s.label}</span>
                </button>
                {i < steps.length - 1 && (
                  <span className='h-px w-4 bg-secondary' />
                )}
              </li>
            );
          })}
        </ol>
        <MatchCountChip count={matchCount} isLoading={isLoading} />
      </div>
    </div>
  );
}
